import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CardBody,
  Input,
  Button,
  Badge,
  Collapse,
  Table,
  Label,
} from "reactstrap";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import Select from "react-select";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { useDispatch, useSelector } from "react-redux";
import { getCoursePendingEnrollTrainees } from "action/course";
import { uploadTraineeDocuments } from "action/trainee";

const EMPTY_FILTERS = {
  searchName: "",
  searchStatus: null,
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Trainees" },
  { value: "COMPLETE", label: "Fully Approved" },
  { value: "PENDING", label: "Pending Review" },
  { value: "ACTION_REQUIRED", label: "Action Required" },
];

const DOC_STATUS_META = {
  APPROVED: {
    label: "Approved",
    className: "status-approved",
    icon: "fa-solid fa-circle-check",
  },
  PENDING: {
    label: "Pending",
    className: "status-pending",
    icon: "fa-solid fa-clock",
  },
  REJECTED: {
    label: "Rejected",
    className: "status-rejected",
    icon: "fa-solid fa-circle-xmark",
  },
  DELETED: {
    label: "Deleted",
    className: "status-deleted",
    icon: "fa-solid fa-trash",
  },
  EXPIRED: {
    label: "Expired",
    className: "status-expired",
    icon: "fa-solid fa-hourglass-end",
  },
  NOT_UPLOADED: {
    label: "Not Uploaded",
    className: "status-not-uploaded",
    icon: "fa-regular fa-circle",
  },
};

const DOC_STATUS_BADGE_COLOR = {
  APPROVED: "success",
  PENDING: "warning",
  REJECTED: "danger",
  DELETED: "secondary",
  EXPIRED: "dark",
  NOT_UPLOADED: "secondary",
};

// const ENROLLMENT_STATUS_META = {
//   NOT_ENROLLED: { label: "Not Enrolled", className: "enroll-status-pending" },
//   ENROLLMENT_FAILED: {
//     label: "Enrollment Failed",
//     className: "enroll-status-failed",
//   },
// };

const ACTION_REQUIRED_STATUSES = ["REJECTED", "DELETED", "EXPIRED"];

const getFileNameFromPath = (path) => {
  if (!path) return null;
  const segments = path.split("/");
  return segments[segments.length - 1] || path;
};

const mapDocumentsForTrainee = (apiDocuments = [], requiredDocs = []) =>
  requiredDocs.map((reqDoc, index) => {
    const docKey = reqDoc.courseDocumentID ?? reqDoc.ID ?? index;
    const name = reqDoc.courseDocumentName || `Document ${index + 1}`;

    const apiDoc = apiDocuments.find(
      (d) => String(d.document_id) === String(docKey),
    );

    if (!apiDoc) {
      return {
        id: `doc-${docKey}`,
        courseDocumentID: docKey,
        name,
        status: "NOT_UPLOADED",
        fileName: null,
        documentPath: null,
        remark: null,
      };
    }

    return {
      id: `doc-${docKey}`,
      courseDocumentID: docKey,
      name,
      status: apiDoc.status || "NOT_UPLOADED",
      fileName: getFileNameFromPath(apiDoc.document_path),
      documentPath: apiDoc.document_path || null,
      remark: apiDoc.remark || null,
    };
  });

const mapApiTrainee = (apiTrainee, requiredDocs) => ({
  ID: apiTrainee.trainee_id,
  uUID: apiTrainee.trainee_id != null ? `#${apiTrainee.trainee_id}` : null,
  user: {
    firstName: apiTrainee.first_name,
    lastName: apiTrainee.last_name,
    email: apiTrainee.email,
    mobile: apiTrainee.mobile,
  },
  organizationName: apiTrainee.organization_name || null,
  enrollmentStatus: apiTrainee.enrollment_status || null,
  profileImagePath: apiTrainee.profile_image_path || null, // Add profile image path
  documents: mapDocumentsForTrainee(apiTrainee.documents, requiredDocs),
});

const getInitial = (firstName) =>
  firstName ? firstName.charAt(0).toUpperCase() : "?";

// Helper function to render avatar with profile image or initials
// const renderAvatar = (firstName, lastName, profileImagePath) => {
//   const fullName = `${firstName || ""} ${lastName || ""}`.trim();
//   const initials = getInitial(firstName);

//   // Check if profile image path exists and is not empty
//   if (profileImagePath && profileImagePath.trim() !== "") {
//     return (
//       <img
//         src={profileImagePath}
//         alt={fullName || "Trainee"}
//         className="trainee-avatar-image"
//         onError={(e) => {
//           // If image fails to load, show initials instead
//           e.target.style.display = "none";
//           e.target.parentNode.innerHTML = initials;
//         }}
//       />
//     );
//   }

//   return initials;
// };

const getOverallStatus = (documents) => {
  const total = documents.length;
  const approvedCount = documents.filter((d) => d.status === "APPROVED").length;
  const needsAction = documents.some((d) =>
    ACTION_REQUIRED_STATUSES.includes(d.status),
  );

  let statusKey = "PENDING";
  if (approvedCount === total) statusKey = "COMPLETE";
  else if (needsAction) statusKey = "ACTION_REQUIRED";

  return { approvedCount, total, statusKey };
};

const PrerequisiteDocumentsTab = ({
  courseId,
  requiredDocuments = [],
  onSelectedTraineesChange,
  onSelectedTraineesStatusChange, // New prop to pass document approval status
}) => {
  const { notify } = useContext(NotificationContext);
  const hasRequiredDocuments = requiredDocuments.length > 0;
  const dispatch = useDispatch();

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [expandedId, setExpandedId] = useState(null);

  const [selectedTrainees, setSelectedTrainees] = useState([]);

  const [stagedFiles, setStagedFiles] = useState({});
  const [submittingTraineeId, setSubmittingTraineeId] = useState(null);

  const pendingEnrollListResponse = useSelector(
    (state) => state.course.channelPartnerCoursePendingEnrolledList,
  );

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
  });

  const isFiltered =
    !!appliedFilters.searchName || !!appliedFilters.searchStatus?.value;

  const buildFilterParams = useCallback((filters) => {
    const filterList = [];
    if (filters.searchStatus?.value) {
      filterList.push({
        field: "DocumentStatus",
        condition: "eq",
        filterValues: [filters.searchStatus.value],
      });
    }
    return { filters: filterList.length ? JSON.stringify(filterList) : "" };
  }, []);

  const fetchPendingEnrolledTraineeList = useCallback(() => {
    setListState({ isLoading: true, error: "" });

    getCoursePendingEnrollTrainees(
      courseId,
      dispatch,
      pageNum,
      buildFilterParams(appliedFilters),
      appliedFilters.searchName,
      {
        sortBy: "CreatedAt",
        sortType: "desc",
      },
    );
  }, [courseId, dispatch, pageNum, appliedFilters, buildFilterParams]);

  useEffect(() => {
    if (courseId) {
      fetchPendingEnrolledTraineeList();
    }
  }, [courseId, fetchPendingEnrolledTraineeList]);

  useEffect(() => {
    if (pendingEnrollListResponse?.code === 200) {
      setListState({ isLoading: false, error: "" });
    } else if (
      pendingEnrollListResponse?.code ||
      pendingEnrollListResponse?.message
    ) {
      setListState({
        isLoading: false,
        error:
          pendingEnrollListResponse?.message ||
          "Failed to load trainees. Please try again.",
      });
    }
  }, [pendingEnrollListResponse]);

  const apiTrainees = pendingEnrollListResponse?.data?.trainees || [];
  const totalRecords = pendingEnrollListResponse?.data?.total_records || 0;
  const recordsPerPage =
    pendingEnrollListResponse?.data?.no_of_records_per_page ||
    DEFAULT_RECORDS_PER_PAGE;

  const trainees = useMemo(
    () =>
      apiTrainees.map((apiTrainee) =>
        mapApiTrainee(apiTrainee, requiredDocuments),
      ),
    [apiTrainees, requiredDocuments],
  );

  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
  const noRecords =
    !listState.isLoading && !listState.error && totalRecords === 0;

  const formattedList = trainees.map((item) => {
    const { approvedCount, total, statusKey } = getOverallStatus(
      item.documents,
    );
    return {
      ...item,
      fullName:
        `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
      approvedCount,
      total,
      statusKey,
    };
  });

  // Check if all selected trainees have all documents approved
  const checkAllSelectedTraineesApproved = useCallback(() => {
    if (selectedTrainees.length === 0) return false;
    if (!hasRequiredDocuments) return true;

    const selectedTraineeData = formattedList.filter((t) =>
      selectedTrainees.includes(t.ID),
    );
    if (selectedTraineeData.length === 0) return false;

    // Check if every selected trainee has all documents approved
    return selectedTraineeData.every((trainee) =>
      trainee.documents.every((doc) => doc.status === "APPROVED"),
    );
  }, [selectedTrainees, formattedList, hasRequiredDocuments]);

  // Notify parent component about document approval status
  useEffect(() => {
    const allApproved = checkAllSelectedTraineesApproved();
    onSelectedTraineesStatusChange?.(allApproved);
  }, [
    selectedTrainees,
    checkAllSelectedTraineesApproved,
    onSelectedTraineesStatusChange,
  ]);

  const handleSearch = () => {
    setAppliedFilters(filterState);
    setPageNum(1);
  };

  const clearSearch = () => {
    setFilterState(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const currentPageIds = formattedList.map((t) => t.ID);
  const isAllSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedTrainees.includes(id));

  const handleTraineeSelect = (traineeId) => {
    setSelectedTrainees((prev) => {
      const updatedIds = prev.includes(traineeId)
        ? prev.filter((id) => id !== traineeId)
        : [...prev, traineeId];

      onSelectedTraineesChange?.(updatedIds);
      return updatedIds;
    });
  };

  const handleSelectAll = () => {
    let updatedIds;
    if (isAllSelected) {
      updatedIds = selectedTrainees.filter((id) => !currentPageIds.includes(id));
    } else {
      updatedIds = [...new Set([...selectedTrainees, ...currentPageIds])];
    }

    setSelectedTrainees(updatedIds);
    onSelectedTraineesChange?.(updatedIds);
  };

  const handleFileStage = (traineeId, docId, file) => {
    if (!file) return;
    setStagedFiles((prev) => ({
      ...prev,
      [traineeId]: {
        ...(prev[traineeId] || {}),
        [docId]: file,
      },
    }));
  };

  const handleClearStagedForTrainee = (traineeId) => {
    setStagedFiles((prev) => {
      const next = { ...prev };
      delete next[traineeId];
      return next;
    });
  };

  const handleSubmitDocuments = async (traineeId) => {
    const traineeStaged = stagedFiles[traineeId];
    const docIds = traineeStaged ? Object.keys(traineeStaged) : [];

    if (docIds.length === 0) {
      notify("warning", "Please select at least one document to upload.");
      return;
    }

    const formData = new FormData();
    let hasFiles = false;

    docIds.forEach((docId) => {
      const file = traineeStaged[docId];
      if (file) {
        const documentId = docId.replace("doc-", "");
        formData.append("documentId", documentId);
        formData.append("document", file);
        hasFiles = true;
      }
    });

    if (!hasFiles) {
      notify("warning", "Please select at least one document to upload.");
      return;
    }

    setSubmittingTraineeId(traineeId);
    try {
      const res = await uploadTraineeDocuments(traineeId, formData);

      if (res?.code === 200 || res?.status === "SUCCESS") {
        notify("success", res?.message || "Documents uploaded successfully.");
        handleClearStagedForTrainee(traineeId);
        fetchPendingEnrolledTraineeList();
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to upload documents.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while uploading documents.",
      );
    } finally {
      setSubmittingTraineeId(null);
    }
  };

  const handleViewDocument = (doc, stagedFile) => {
    if (stagedFile) {
      const url = URL.createObjectURL(stagedFile);
      window.open(url, "_blank");
      return;
    }
    if (doc.documentPath) {
      window.open(doc.documentPath, "_blank");
      return;
    }
    notify("info", `Preview for "${doc.name}" isn't available yet.`);
  };

  // const getDocumentStatusInfo = (trainee) => {
  //   if (!hasRequiredDocuments) return null;
  //   if (trainee.total === 0) return null;
  //   if (trainee.approvedCount === trainee.total) {
  //     return {
  //       type: "success",
  //       message: "All documents approved",
  //       icon: "fa-check-circle",
  //     };
  //   }
  //   if (trainee.statusKey === "ACTION_REQUIRED") {
  //     return {
  //       type: "warning",
  //       message: `${trainee.total - trainee.approvedCount} document(s) need attention`,
  //       icon: "fa-exclamation-triangle",
  //     };
  //   }
  //   return {
  //     type: "info",
  //     message: `${trainee.approvedCount}/${trainee.total} approved`,
  //     icon: "fa-clock",
  //   };
  // };

  return (
    <>
      <CardBody>
        {/* Filter Section */}
        <div className="filter-section-modern">
          <div className="filter-row">
            <div className="filter-input-wrapper">
              <i className="fas fa-search filter-icon" />
              <Input
                type="text"
                placeholder="Search trainees by name..."
                className="filter-input-modern"
                value={filterState.searchName}
                onChange={(e) =>
                  setFilterState((prev) => ({
                    ...prev,
                    searchName: e.target.value,
                  }))
                }
                onKeyDown={handleKeyDown}
              />
            </div>

            {hasRequiredDocuments && (
              <div className="filter-select-wrapper">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  placeholder="Filter by status"
                  classNamePrefix="react-select-modern"
                  value={filterState.searchStatus}
                  onChange={(selected) =>
                    setFilterState((prev) => ({
                      ...prev,
                      searchStatus: selected,
                    }))
                  }
                  onKeyDown={handleKeyDown}
                  isClearable={true}
                  styles={fixedWidthCustomStyle()}
                />
              </div>
            )}

            <Button color="deep-blue" onClick={handleSearch}>
              <i className="fas fa-search mr-2" />
              Search
            </Button>

            {isFiltered && (
              <Button className="clear-filter-btn" onClick={clearSearch}>
                <i className="fas fa-times mr-2" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Stats & Actions Bar */}
        {!listState.isLoading && !listState.error && totalRecords > 0 && (
          <div className="stats-actions-bar">
            <div className="stats-left">
              {!noRecords && (
                <Label className="select-all-wrapper">
                  <input
                    type="checkbox"
                    className="select-all-checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                  <span className="select-all-label">Select All</span>
                </Label>
              )}
            </div>

            <div className="stats-right">
              {selectedTrainees.length > 0 && (
                <span className="stats-badge mr-3">
                  <i className="fas fa-check-square mr-2" />
                  {selectedTrainees.length} Trainees Selected
                </span>
              )}
              <span className="stats-badge">
                <i className="fas fa-users mr-2" />
                Total {totalRecords} Trainee{totalRecords > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        )}

        {/* Loading */}
        {listState.isLoading ? (
          <div className="loaderContainer">
            <i className="fa fa-spinner fa-spin fa-2xl loader" />
          </div>
        ) : listState.error ? (
          <div className="loaderContainer error-style">
            <div className="error-icon">
              <i className="fa-solid fa-circle-exclamation" />
            </div>
            <span>{listState.error}</span>
          </div>
        ) : noRecords ? (
          <div className="empty-state-modern">
            <div className="empty-state-icon">
              <i className="fas fa-inbox" />
            </div>
            <span>No trainees found</span>
            {isFiltered && (
              <p className="empty-state-subtitle">
                Try adjusting your search or filter criteria
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Trainee Cards */}
            <div className="trainee-cards-modern">
              {formattedList.map((trainee) => {
                const isExpanded = expandedId === trainee.ID;
                const progressPct = trainee.total
                  ? (trainee.approvedCount / trainee.total) * 100
                  : 0;
                const isSelected = selectedTrainees.includes(trainee.ID);

                const traineeStagedFiles = stagedFiles[trainee.ID] || {};
                const stagedCount = Object.keys(traineeStagedFiles).length;
                const isSubmittingThisTrainee =
                  submittingTraineeId === trainee.ID;
                // const enrollMeta =
                //   trainee.enrollmentStatus &&
                //   ENROLLMENT_STATUS_META[trainee.enrollmentStatus];

                // const docStatusInfo = getDocumentStatusInfo(trainee);
                const allDocsApproved = trainee.documents.every(
                  (doc) => doc.status === "APPROVED",
                );

                return (
                  <div
                    key={trainee.ID}
                    className={`trainee-card-modern ${allDocsApproved ? "trainee-card-approved" : "trainee-card-pending"}`}
                  >
                    {/* Card Header */}
                    <div
                      className="trainee-card-header"
                      onClick={() =>
                        hasRequiredDocuments && toggleExpand(trainee.ID)
                      }
                      role="button"
                      tabIndex={0}
                    >
                      <div className="card-header-left">
                        <Label
                          className="checkbox-wrapper"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Input
                            type="checkbox"
                            className="trainee-checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleTraineeSelect(trainee.ID);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="checkbox-custom">
                            <i className="fas fa-check" />
                          </span>
                        </Label>

                        <div className="trainee-avatar-modern">
                          {/* Updated avatar with profile image support */}
                          {trainee.profileImagePath &&
                          trainee.profileImagePath.trim() !== "" ? (
                            <img
                              src={trainee.profileImagePath}
                              alt={trainee.fullName || "Trainee"}
                              className="trainee-avatar-image"
                              onError={(e) => {
                                // If image fails to load, show initials instead
                                e.target.style.display = "none";
                                const parent = e.target.parentNode;
                                parent.textContent = getInitial(
                                  trainee.user?.firstName,
                                );
                              }}
                            />
                          ) : (
                            getInitial(trainee.user?.firstName)
                          )}
                        </div>

                        <div className="trainee-info-modern">
                          <div className="trainee-name-modern">
                            {trainee.fullName}
                            {trainee.uUID && (
                              <span className="trainee-id-modern">
                                • {trainee.uUID}
                              </span>
                            )}
                            {/* {enrollMeta && (
                              <Badge pill className={`ml-2 enroll-status-badge ${enrollMeta.className}`}>
                                {enrollMeta.label}
                              </Badge>
                            )} */}
                            {/* Document status badge */}
                            {/* {docStatusInfo && (
                              <Badge 
                                color={docStatusInfo.type === 'success' ? 'success' : docStatusInfo.type === 'warning' ? 'warning' : 'info'} 
                                pill 
                                className="ml-2 doc-status-badge"
                              >
                                <i className={`fas ${docStatusInfo.icon} mr-1`} />
                                {docStatusInfo.message}
                              </Badge>
                            )} */}
                          </div>
                          <div className="trainee-details-modern">
                            <span>
                              <i className="fas fa-envelope mr-1" />
                              {trainee.user?.email}
                            </span>
                            <span className="detail-separator">|</span>
                            <span>
                              <i className="fas fa-phone mr-1" />
                              {trainee.user?.mobile}
                            </span>
                            {trainee.organizationName && (
                              <>
                                <span className="detail-separator">|</span>
                                <span>
                                  <i className="fas fa-building mr-1" />
                                  {trainee.organizationName}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="card-header-right">
                        {hasRequiredDocuments ? (
                          <div className="progress-modern">
                            <span className="progress-label">
                              {trainee.approvedCount}/{trainee.total} Approved
                            </span>
                            <div className="progress-bar-modern">
                              <div
                                className={`progress-fill-modern ${allDocsApproved ? "progress-fill-success" : "progress-fill-warning"}`}
                                style={{ width: `${progressPct}%` }}
                                data-label={`${Math.round(progressPct)}%`}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="stats-badge text-sm">
                            <i className="fas fa-check-circle mr-1" />
                            No documents required
                          </span>
                        )}

                        {hasRequiredDocuments && (
                          <i
                            className={`fas fa-chevron-${isExpanded ? "up" : "down"} accordion-chevron-modern`}
                          />
                        )}
                      </div>
                    </div>

                    {/* Card Body - Expanded */}
                    <Collapse isOpen={isExpanded}>
                      <div className="trainee-card-body">
                        {trainee.documents.length === 0 ? (
                          <div className="doc-empty-state-clean py-3">
                            <i className="fa fa-check-circle doc-empty-icon-clean"></i>
                            <h6 className="doc-empty-title-clean">
                              No Documents Required
                            </h6>
                            <p className="doc-empty-subtitle-clean mb-0">
                              This course doesn't require prerequisite documents
                              for enrollment.
                            </p>
                          </div>
                        ) : (
                          <>
                            <Table
                              className="documents-table-modern"
                              responsive
                            >
                              <thead>
                                <tr>
                                  <th>Document Name</th>
                                  <th>Status</th>
                                  <th>Remark</th>
                                  <th className="text-right">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {trainee.documents.map((doc) => {
                                  const docMeta =
                                    DOC_STATUS_META[doc.status] ||
                                    DOC_STATUS_META.NOT_UPLOADED;
                                  const stagedDoc = traineeStagedFiles[doc.id];
                                  const canReupload =
                                    doc.status !== "APPROVED" &&
                                    doc.status !== "PENDING";
                                  const isApproved = doc.status === "APPROVED";

                                  return (
                                    <tr
                                      key={doc.id}
                                      className={
                                        isApproved
                                          ? "doc-row-approved"
                                          : "doc-row-pending"
                                      }
                                    >
                                      <td>
                                        <span className="doc-name-modern">
                                          {doc.name}
                                          {isApproved && (
                                            <i className="fas fa-check-circle text-success ml-2" />
                                          )}
                                        </span>
                                      </td>
                                      <td>
                                        <Badge
                                          color={
                                            DOC_STATUS_BADGE_COLOR[
                                              doc.status
                                            ] || "secondary"
                                          }
                                          pill
                                          className="doc-status-modern"
                                        >
                                          {docMeta.label}
                                        </Badge>
                                      </td>
                                      <td>
                                        <span className="doc-date-modern">
                                          {doc.remark || "—"}
                                        </span>
                                      </td>
                                      <td className="text-right">
                                        <div className="doc-actions-modern">
                                          {doc.status === "APPROVED" ? (
                                            <span className="verified-badge-modern">
                                              <i className="fas fa-check-circle" />
                                              Verified
                                            </span>
                                          ) : doc.status === "PENDING" ? (
                                            <span className="pending-badge-modern">
                                              <i className="fas fa-clock" />
                                              Approval Pending
                                            </span>
                                          ) : (
                                            <>
                                              {stagedDoc && (
                                                <Button
                                                  color="link"
                                                  size="sm"
                                                  className="btn-view-modern"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDocument(
                                                      doc,
                                                      stagedDoc,
                                                    );
                                                  }}
                                                >
                                                  <i className="fas fa-eye" />{" "}
                                                  View
                                                </Button>
                                              )}
                                              {canReupload && (
                                                <label
                                                  className={`btn btn-primary btn-sm btn-upload-modern ${
                                                    stagedDoc
                                                      ? "btn-upload-replace"
                                                      : ""
                                                  }`}
                                                  style={
                                                    isSubmittingThisTrainee
                                                      ? {
                                                          opacity: 0.6,
                                                          pointerEvents: "none",
                                                        }
                                                      : undefined
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <input
                                                    type="file"
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    hidden
                                                    onChange={(e) => {
                                                      const file =
                                                        e.target.files?.[0];
                                                      handleFileStage(
                                                        trainee.ID,
                                                        doc.id,
                                                        file,
                                                      );
                                                      e.target.value = "";
                                                    }}
                                                  />
                                                  <i className="fas fa-upload" />
                                                  {stagedDoc
                                                    ? " Change Document"
                                                    : " Upload"}
                                                </label>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>

                            {/* Submit area - appears once files are staged for this trainee */}
                            {stagedCount > 0 && (
                              <div className="doc-submit-area-modern mt-3">
                                <div className="doc-submit-actions">
                                  <Button
                                    size="sm"
                                    className="btn-submit-docs-modern"
                                    disabled={isSubmittingThisTrainee}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubmitDocuments(trainee.ID);
                                    }}
                                  >
                                    {isSubmittingThisTrainee ? (
                                      <>
                                        <i className="fas fa-spinner fa-spin mr-2" />{" "}
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-upload mr-2" />
                                        {stagedCount === 1
                                          ? "Submit Document"
                                          : "Submit Documents"}
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="btn-clear-docs-modern"
                                    disabled={isSubmittingThisTrainee}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearStagedForTrainee(trainee.ID);
                                    }}
                                  >
                                    <i className="fas fa-times mr-1" /> Clear
                                    All
                                  </Button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="pagination-modern">
              <span />
              <CustomPagination
                totalPages={totalPages}
                setPageNum={setPageNum}
                activePage={pageNum}
              />
            </div>
          </>
        )}
      </CardBody>
    </>
  );
};

export default PrerequisiteDocumentsTab;
